import { createContext, useCallback, useContext, useState } from "react";
import { Split, ExerciseEntry } from "../types/Programmazione";
import {
  PrecompiledSet,
  PrecompiledWeek,
  PrecompiledProgression,
  PrecompiledWeekProgressionRule,
  ProgressionMethodConfig,
} from "../types/Entities";

function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

type ClipboardState = {
  copiedSplit: Split | null;
  copiedExercise: ExerciseEntry | null;
  copiedSplits: Split[] | null;
  // PROG-03 F3 PreFE: independent slots for the precompiled-progression
  // editor. PrecompiledSet / PrecompiledWeek are different data models from
  // SetItem / Week (per the 420a89b9 discussion), so they get their own
  // clipboard slots — pasting a programmazione set into PreFE or vice-versa
  // is not supported (would silently drop fields).
  copiedPrecompiledSet: PrecompiledSet | null;
  copiedPrecompiledWeek: PrecompiledWeek | null;
  // FE-M (#97065f8b) — full progression copy slot. Paste replaces the form's
  // entire `weeks` array but leaves label/description/tags intact (handled at
  // the consumer site, see PrecompiledProgressionForm).
  copiedPrecompiledProgression: PrecompiledProgression | null;
  // FE-Q (#873010fd) — whole-rule copy slot for precompiled progressions.
  // Independent of copiedPrecompiledSet so the user can copy just the future-
  // progression rule (with its soglie) without overwriting the target set's
  // intrinsic params (label/repScheme/effort/etc.). The paste site clones
  // and runs normalizePastedRule before replacing the rule on the target set.
  copiedPrecompiledRule: PrecompiledWeekProgressionRule | null;
  // Per-box copy slot for the StS / SiS progression-method panels inside
  // ProgressionRuleEditor. Holds the canonical suffix-free config so a config
  // copied from a "Set to Set" box can be pasted into a "Set in Set" box and
  // vice-versa (mapping in precompiledRuleApply.ts). Independent of
  // copiedPrecompiledRule (whole-rule) so the two clipboards don't clobber.
  copiedPrecompiledRuleBox: ProgressionMethodConfig | null;
};

type ClipboardContextType = ClipboardState & {
  copySplit: (split: Split) => void;
  copyExercise: (exercise: ExerciseEntry) => void;
  copySplits: (splits: Split[]) => void;
  copyPrecompiledSet: (set: PrecompiledSet) => void;
  copyPrecompiledWeek: (week: PrecompiledWeek) => void;
  copyPrecompiledProgression: (prog: PrecompiledProgression) => void;
  copyPrecompiledRule: (rule: PrecompiledWeekProgressionRule) => void;
  copyPrecompiledRuleBox: (config: ProgressionMethodConfig) => void;
};

const ClipboardContext = createContext<ClipboardContextType | null>(null);

export function ClipboardProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ClipboardState>({
    copiedSplit: null,
    copiedExercise: null,
    copiedSplits: null,
    copiedPrecompiledSet: null,
    copiedPrecompiledWeek: null,
    copiedPrecompiledProgression: null,
    copiedPrecompiledRule: null,
    copiedPrecompiledRuleBox: null,
  });

  const copySplit = useCallback((split: Split) => {
    setState((prev) => ({ ...prev, copiedSplit: deepClone(split) }));
  }, []);

  const copyExercise = useCallback((exercise: ExerciseEntry) => {
    setState((prev) => ({ ...prev, copiedExercise: deepClone(exercise) }));
  }, []);

  const copySplits = useCallback((splits: Split[]) => {
    setState((prev) => ({ ...prev, copiedSplits: deepClone(splits) }));
  }, []);

  const copyPrecompiledSet = useCallback((set: PrecompiledSet) => {
    setState((prev) => ({ ...prev, copiedPrecompiledSet: deepClone(set) }));
  }, []);

  const copyPrecompiledWeek = useCallback((week: PrecompiledWeek) => {
    setState((prev) => ({ ...prev, copiedPrecompiledWeek: deepClone(week) }));
  }, []);

  const copyPrecompiledProgression = useCallback((prog: PrecompiledProgression) => {
    setState((prev) => ({ ...prev, copiedPrecompiledProgression: deepClone(prog) }));
  }, []);

  const copyPrecompiledRule = useCallback((rule: PrecompiledWeekProgressionRule) => {
    setState((prev) => ({ ...prev, copiedPrecompiledRule: deepClone(rule) }));
  }, []);

  const copyPrecompiledRuleBox = useCallback((config: ProgressionMethodConfig) => {
    setState((prev) => ({ ...prev, copiedPrecompiledRuleBox: deepClone(config) }));
  }, []);

  return (
    <ClipboardContext.Provider
      value={{
        ...state,
        copySplit,
        copyExercise,
        copySplits,
        copyPrecompiledSet,
        copyPrecompiledWeek,
        copyPrecompiledProgression,
        copyPrecompiledRule,
        copyPrecompiledRuleBox,
      }}
    >
      {children}
    </ClipboardContext.Provider>
  );
}

export function useClipboard() {
  const ctx = useContext(ClipboardContext);
  if (!ctx) throw new Error("useClipboard must be used within ClipboardProvider");
  return ctx;
}

// Provider-tolerant variant: returns null when there is no ClipboardProvider in
// the tree instead of throwing. Used by components that may be mounted outside
// the provider (e.g. in isolated unit tests) but want clipboard features when
// it is present.
export function useClipboardOptional(): ClipboardContextType | null {
  return useContext(ClipboardContext);
}
